import { gql } from 'graphql-request';
import { onMount, createSignal, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Button } from '../../ui-components/button/button';
import { Modal } from '../../ui-components/modal/modal';
import { Text } from '../../ui-components/text/text';
import { StyledVerticalSpace } from '../../ui-components/utility-style-components/spacing';
import { StyledButtonRow, StyledUserWarning, StyledDivider } from './user-warning.styles';
import { Heading } from '../../ui-components/heading/heading';
import { createStore } from 'solid-js/store';
import { useNavigate } from '@solidjs/router';

export const GET_USER_WARNING_QUERY = gql`
    query {
        settings: resource(externalId: "user-warning-settings") {
            content
        }
    }
`;

type UserWarningProps = {
    isUserContent: boolean;
};

export const UserWarning = (props: UserWarningProps) => {
    const {
        createCachedResource,
        hcpWarning,
        userWarning,
        registerUserWarningSeen,
        userState,
        appStore,
    } = useContext(AppContext);

    const [showModal, setShowModal] = createSignal(false);
    const [message, setMessage] = createStore({
        heading: '',
        body: '',
        prompt: '',
        rejectButtonText: '',
        acceptButtonText: '',
        userWarningRejectPageUrl: '',
    });

    const navigate = useNavigate();

    const [ userWarningSettings ] = createCachedResource(GET_USER_WARNING_QUERY, {}, true);

    onMount(() => {

        if(!shouldShowUserWarning()) {
            return;
        }

        const settings = userWarningSettings()?.settings?.content;

        if (!settings) {
            return;
        }

        const {
            enabled,
            heading,
            body,
            prompt,
            rejectButtonText,
            acceptButtonText,
            userWarningRejectPage
        } = settings;

        if (!enabled) {
            return;
        }

        if(userState.user?.email) {
            return;
        }

        if (userWarning.seen) {
            return;
        }

        if (hcpWarning.seen) {
            return;
        }

        setMessage({
            heading,
            body,
            prompt,
            rejectButtonText,
            acceptButtonText,
            userWarningRejectPageUrl: userWarningRejectPage?.url,
        });

        setTimeout(() => {
            setShowModal(true);
        }, 2000);
    });

    const shouldShowUserWarning = () => {
        if (appStore.isHcp()) {
            return false;
        }

        return Boolean(props?.isUserContent);
    };

    const accept = () => {
        saveCookie();
        registerUserWarningSeen();
        setShowModal(false);
    };

    const reject = () => {
        setShowModal(false);
        let rejectUrl = message.userWarningRejectPageUrl ? message.userWarningRejectPageUrl : '/';

        if (rejectUrl.endsWith('/')) {
            rejectUrl = rejectUrl.slice(0, -1);
        }

        navigate(rejectUrl);
    };

    const saveCookie = async () => {
        await fetch('/set-user-warning-seen', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ seenUserWarning: true }),
        });
    };

    return (
        <Modal visible={showModal()} onClose={() => reject()} size='normal' darkenBackground={true}>
            <StyledVerticalSpace size={3} />
            <StyledUserWarning>
                <Heading tag="h3">
                    {message.heading ? message.heading : 'You are now entering healthcare professional content only'}
                </Heading>

                <StyledDivider />

                <Text fontSize="small" color="gray">
                    {message.body ? message.body : `Please note that you are about to enter a section of the website that is intended for healthcare professionals and
                        purchasing agents only.`}
                </Text>

                <Text fontSize="normal" fontStyle="bold" color="gray">
                    {message.prompt ? message.prompt : 'Are you a healtchcare professional or a hospital purchasing agent?'}
                </Text>

                <StyledButtonRow>
                    <Button variant="secondary" label={message.rejectButtonText ? message.rejectButtonText : 'No I am not'} onClick={reject} />
                    <Button variant="primary" label={message.acceptButtonText ? message.acceptButtonText : 'Yes I am'} onClick={accept} />
                </StyledButtonRow>
            </StyledUserWarning>
        </Modal>
    );
};
